import CustomAutocomplete from "@/components/Form/InputFields/CustomAutocomplete";
import { useUserContext } from "@/context/UserContext";
import { useAdvertisersList } from "@/modules/Advertiser/advertiser.hook";
import { useListInsightAudience } from "@/modules/Asset/Creative/asset.hook";
import { Channel, InputFieldType } from "@/types";
import { Chip, Grid, Typography } from "@mui/material";
import { useMemo } from "react";
import { useWatch } from "react-hook-form";
import { IFormProps } from "../campaign.interface";
import { CampaignType, radioOptions } from "./data";

export default function PrimaryDetails({
  control,
  errors,
  advertiserId,
  isEdit = false,
  type,
}: PrimaryDetails) {
  const { loggedInAdvertiserId } = useUserContext();
  const { data: advertisers, isLoading: advertisersLoading } =
    useAdvertisersList();
  const watchExecutionType = useWatch({
    control,
    name: "executionType",
  });
  /*
    The audience is locked on edit, so the field holds the SAVED one. Passed as
    `includeIds` so an attached paused pixel audience still resolves to its
    name (the backend never lists a paused one otherwise); create sends none.
  */
  const savedAudienceId = useWatch({ control, name: "audienceIds" });
  const includeIds = useMemo(
    () =>
      isEdit && Number(savedAudienceId) > 0 ? [Number(savedAudienceId)] : [],
    [isEdit, savedAudienceId]
  );
  const {
    data: audienceList,
    isLoading: audienceListLoading,
    isFetching: audienceListFetching,
  } = useListInsightAudience({
    type,
    enabled: Boolean(advertiserId) && Boolean(watchExecutionType),
    queryParams: {
      advertiserId,
      executionType: watchExecutionType,
      includeIds,
    },
  });
  const options = audienceListMapping(audienceList);
  const savedIsPaused = Boolean(
    options?.find((option: AudienceOption) => option.value === savedAudienceId)
      ?.paused
  );

  return (
    <>
      <Grid item xs={6}>
        <CustomAutocomplete
          name="advertiserCompanyId"
          label="Select Advertiser"
          control={control}
          itemList={advertisersList(advertisers)}
          error={errors?.advertiserCompanyId?.message}
          required={true}
          loading={advertisersLoading}
          disabled={Boolean(loggedInAdvertiserId) || isEdit}
          type="secondary"
        />
      </Grid>
      <Grid item xs={6}>
        <CustomAutocomplete
          name="executionType"
          label="Campaign Type"
          control={control}
          itemList={CampaignType}
          required={true}
          disableClearable
          type="secondary"
          disabled={isEdit}
        />
      </Grid>
      <Grid item xs={6}>
        <CustomAutocomplete
          name="audienceIds"
          label="Audience"
          itemList={options}
          control={control}
          type="secondary"
          required={true}
          error={errors?.audienceIds?.message}
          loading={audienceListLoading && audienceListFetching}
          disabled={isEdit || !watchExecutionType}
          renderOptionAdornment={(value: number) =>
            options?.find((option: AudienceOption) => option.value === value)
              ?.paused ? (
              <Chip
                label="Paused"
                size="small"
                color="warning"
                variant="outlined"
              />
            ) : null
          }
        />
        {savedIsPaused && (
          <Typography
            variant="caption"
            color="warning.main"
            component="p"
            sx={{ mt: 0.5, mx: 1.5 }}
          >
            This audience is paused. The campaign keeps it, but its segment is
            no longer refreshed until it&apos;s resumed.
          </Typography>
        )}
      </Grid>
      <Grid item xs={6}>
        <CustomAutocomplete
          name="dateType"
          label="Campaign Period"
          control={control}
          itemList={radioOptions}
          required={true}
          disableClearable
          type="secondary"
          disabled={isEdit}
        />
      </Grid>
    </>
  );
}

type AudienceOption = { label: string; value: number; paused: boolean };

// A single-select's input shows only the label, so a paused audience carries
// the tag in its label too, not just in the dropdown pill.
function audienceListMapping(data: any): AudienceOption[] | undefined {
  return data?.rows?.map((item: any) => ({
    label: item?.pausedAt ? `${item?.segmentName} · Paused` : item?.segmentName,
    value: item?.id,
    paused: Boolean(item?.pausedAt),
  }));
}

function advertisersList(data: any) {
  return data
    ?.filter((item: any) => item.dspAccountId !== null)
    .map((item: any) => ({ label: item.name, value: item.id }));
}
interface PrimaryDetails extends IFormProps {
  type: Channel;
  isMultiple?: boolean;
  noContainer?: boolean;
  inputType?: InputFieldType;
  name?: string;
  errorMessage?: string;
  advertiserId: number;
  audienceType?: any;
  activeStep?: any;
  switchActive?: any;
  setSwitchActive?: any;
  isEdit: any;
}
